"use client";

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { DynamicIcon, IconName } from 'lucide-react/dynamic';
import navLinks from "@/data/navLinks2.json";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DropdownMenuSeparator } from '@radix-ui/react-dropdown-menu';
import Icon, { ComponentName, DynamicIconRenderer } from '@/lib/icons';
import { LogOut, ChevronLeft, ChevronRight, X, EllipsisVertical } from 'lucide-react'; 

interface NavigationItem {
  name: string;
  href: string;
  icon?: string; // Lucide icon name
  customIcon?: ComponentName; // Your custom SVG component
}

interface DashboardSidebarProps {
  user: any // Using any for simplicity
}

export default function DashboardSidebar2({ user }: DashboardSidebarProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false); // Mobile drawer state
  const [isExpanded, setIsExpanded] = useState(false); // Desktop width state
  const [openItem, setOpenItem] = useState<string | undefined>("shipping");

  const twStyleNonRef = (isActive: boolean, itemName: string) => {
    let style: string = "";
    if (itemName === "Premiums") {
      style = "text-[1.1rem] [text-shadow:1px_1px_0_#b86507,2px_2px_0_#b86507,3px_3px_0_#b86507,4px_4px_10px_rgba(0,0,0,0.5)] bg-gradient-to-b from-yellow-300 via-yellow-500 to-yellow-600 hover:bg-gradient-to-b hover:from-yellow-600 hover:via-yellow-500 hover:via-80% hover:to-yellow-300 text-white";
    } else if (isActive) {
      style = "text-[1.1rem] bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200";
    } else {
      style = "text-[1.1rem] text-indigo-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900";
    }
    return style;
  };

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  // Automatically expands the sidebar layout first if a user clicks a collapsed parent icon
  const handleParentMenuClick = (itemName: string) => {
    if (!isExpanded) {
      setIsExpanded(true);
      setOpenItem(itemName);
    }
  };
  
  // Overlay for mobile
  const Overlay = () => (
    <div
      className={`z-40 fixed inset-0 bg-black/50 md:hidden transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={() => setIsOpen(false)}
    />
  );

  return (
    <>
      <Overlay />
      {/* Sidebar Container */}
      <aside 
        className={`
          fixed md:static inset-y-0 top-20 left-0 z-50 md:z-0
          bg-white dark:bg-gray-800 shadow-xl md:shadow-md
          flex flex-col transition-all duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-60 md:translate-x-0"}
          ${isExpanded ? "w-64" : "md:w-20 w-64"}
          pt-0 md:pt-2
        `}
      >
        {/* Top Toggle Button (Visible on Desktop, hidden on Mobile) */}
        <div className="hidden md:flex justify-end p-2 border-b dark:border-gray-700">
          <button
            onClick={toggleExpand}
            className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-gray-700 dark:text-gray-200 transition-colors"
            aria-label={isExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            {isExpanded ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        {/* Mobile Toggle Button */}
        <div className="w-full flex justify-end md:hidden">
          <button
            onClick={toggleSidebar}
            className="bg-indigo-600 text-white w-4 pt-2 pb-2 rounded-r-md shadow-lg hover:bg-indigo-700 transition-colors text-right"
            aria-label="Toggle Sidebar"
          >
            {isOpen ? <X size={18} /> : <EllipsisVertical size={18} />}
          </button>
        </div>

        <nav 
          className="flex-grow p-2 space-y-2 overflow-y-auto overflow-x-hidden"
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            if (e.target === e.currentTarget) setOpenItem("");
          }}
        >
          <Accordion
            type="single"
            collapsible
            className="max-w-lg mr-2 md:mr-0"
            value={isExpanded ? openItem : ""} // Force collapse sub-menus when sidebar is collapsed
            onValueChange={setOpenItem}
          >
            {navLinks.map((item) => {
              const isActive = pathname === item.href;

              if (item.href) {
                return (
                  <AccordionItem value={item.name} key={item.name} className="border-none">
                    <AccordionTrigger className="pl-0 pr-0 pt-1 pb-1 [&>svg]:hidden">
                      <Link 
                        href={item.href}
                        className={`flex items-center p-2 w-full rounded-lg transition-colors duration-150 group
                          ${isActive 
                            ? "text-[1.1rem] bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200" 
                            : "text-[1.1rem] text-indigo-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900"
                          }
                        `} 
                        onClick={() => setIsOpen(false)}
                      >
                        <DynamicIcon
                          name={item.icon as IconName} 
                          className={`w-6 h-6 flex-shrink-0 ml-3 ${isExpanded ? "mr-4" : "mx-auto"} ${isActive ? "text-indigo-700" : "text-indigo-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-300"}`} 
                        />
                        {/* Only show labels when expanded */}
                        <span className={`w-64 overflow-hidden font-semibold transition-opacity duration-200 ${isExpanded ? "opacity-100" : "opacity-0 ml-4"}`}>
                          {item.name}
                        </span>
                      </Link>
                    </AccordionTrigger>
                  </AccordionItem>
                );
              } else {
                return (
                  <AccordionItem value={item.name} key={item.name} className="border-none" onClick={() => setIsExpanded(true)}>
                    <AccordionTrigger className={`pl-0 pr-0 pt-1 pb-1 no-underline hover:no-underline ${!isExpanded && "[&>svg]:hidden"}`}>
                      <div className={`flex items-center p-2 w-full rounded-lg transition-colors duration-150 group ${twStyleNonRef(isActive, item.name)}`}>
                        <DynamicIcon
                          name={item.icon as IconName}
                          className={`w-6 h-6 flex-shrink-0 ml-3 ${isExpanded ? "mr-4" : "mx-auto"} ${item.name === "Premiums" ? "text-white drop-shadow-[0_3px_2px_#965205]" : isActive ? "text-indigo-700" : "text-indigo-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-300"}`} 
                        />
                        <span className={`w-64 overflow-hidden font-semibold transition-opacity duration-200 ${isExpanded ? "opacity-100" : "opacity-0 ml-4"}`}>
                          {item.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    
                    {/* Submenu block - Hidden entirely on desktop if the bar is collapsed */}
                    <AccordionContent className={`${(!isExpanded) ? "hidden" : ""} ${item.name === "Premiums" ? "bg-yellow-500" : "bg-indigo-200"} ml-auto mr-auto mb-0 pb-0 border-t-3 border-t-gray-500 border-b-3 border-b-gray-500`}>
                      <div className="flex flex-col">
                        {item['sub-menu']!.map((sm, i) => {
                          const isActiveSub = pathname === sm.href;
                          if (sm.name === 'Separator' && !sm.href) {
                            return <DropdownMenuSeparator key={i} className="mx-2 h-0.5 bg-gray-200" />;
                          } else if (sm.href) {
                            return (
                              <div className="m-1 text-indigo-600" key={sm.name}>
                                <Link
                                  href={sm.href}
                                  className={`flex items-center w-64 p-2 rounded-lg transition-colors duration-150 group
                                    ${isActiveSub
                                      ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200"
                                      : item.name === "Premiums" ? "text-white bg-yellow-500 dark:text-gray-300 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900" : "text-indigo-800 dark:text-gray-300 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900"
                                    }
                                  `}
                                  onClick={() => setIsOpen(false)}
                                >
                                  {(sm as NavigationItem).icon ? (
                                    <DynamicIcon name={(sm as NavigationItem).icon as IconName} className={`w-6 h-6 ml-4 mr-3 ${isActiveSub ? "text-indigo-800" : item.name === "Premiums" ? "text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-300" : "text-indigo-800 group-hover:text-indigo-600 dark:group-hover:text-indigo-300"} text-sm`} />
                                  ) : (
                                    <DynamicIconRenderer name={(sm as NavigationItem).customIcon!} strokeWidth={1} className={`w-6 h-6 ml-4 mr-3 ${isActiveSub ? "text-indigo-800" : item.name === "Premiums" ? "text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-300" : "text-indigo-800 group-hover:text-indigo-600 dark:group-hover:text-indigo-300"} text-sm`} />
                                  )}
                                  <span className={`w-64 overflow-hidden font-semibold transition-opacity duration-200 ${isExpanded ? "opacity-100" : "opacity-0"}`}>{sm.name}</span>
                                </Link>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              }
            })}
          </Accordion>
        </nav>

        {/* Logout Section */}
        <div className="p-4 border-t dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
            <a
                href="/api/auth/logout"
                className={`flex items-center text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 mt-1 transition-colors group ${!isExpanded && "justify-center"}`}
            >
                <LogOut className={`w-5 h-5 text-red-500 flex-shrink-0 transition-all duration-300 ${isExpanded ? "mr-3" : "mx-auto"}`} />
                
                {/* Layout wrapper forcing the logout text block to collapse gracefully */}
                <div className={`transition-all duration-300 ease-in-out flex items-center overflow-hidden whitespace-nowrap ${isExpanded ? "w-auto opacity-100 scale-100" : "md:w-0 md:opacity-0 md:scale-95"}`}>
                <span className="font-semibold text-red-500">
                    Log Out
                </span>
                </div>
            </a>
        </div>
        </aside>
    </>
  );
}
